import { Tabs, Dropdown } from "antd";
import { LeftOutlined, RightOutlined, DownOutlined } from "@ant-design/icons";
import { useState, useRef, useEffect } from "react";
import type { MenuProps } from "antd";
import { useChatStore } from "@stores/chatStore";
import { CHANNELS } from "@common";
import { TAB_CONFIG, type ConversationTabType } from "@constants/conversationTabs";
import styles from "./styles.module.less";

interface ConversationTabsProps {
  activeTab: ConversationTabType;
  onChange: (tab: ConversationTabType) => void;
}

export const ConversationTabs = ({
  activeTab,
  onChange,
}: ConversationTabsProps) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const selectedChannel = useChatStore((state) => state.selectedChannel);

  const visibleTabs = Object.values(TAB_CONFIG).map((config) => ({
    key: config.key,
    label: config.label,
    disabled: selectedChannel === CHANNELS.ZALO && config.requiresFolderStatus,
  }));

  const dropdownTabs: Array<{ key: string; label: string }> = [];

  const isDropdownTabActive = dropdownTabs.some((tab) => tab.key === activeTab);

  const getDropdownLabel = () => {
    if (isDropdownTabActive) {
      const activeDropdownTab = dropdownTabs.find(
        (tab) => tab.key === activeTab
      );
      return activeDropdownTab ? activeDropdownTab.label : "Khác";
    }
    return "Khác";
  };

  const dropdownMenuItems: MenuProps["items"] = dropdownTabs.map((tab) => ({
    key: tab.key,
    label: tab.label,
  }));

  const handleDropdownMenuClick: MenuProps["onClick"] = ({ key }) => {
    onChange(key as ConversationTabType);
  };

  const checkScrollability = () => {
    if (!tabsContainerRef.current) return;

    const navList = tabsContainerRef.current.querySelector(
      ".ant-tabs-nav-list"
    ) as HTMLElement;
    if (!navList) return;

    const { scrollLeft, scrollWidth, clientWidth } = navList;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      checkScrollability();
    }, 100);

    const navList = tabsContainerRef.current?.querySelector(
      ".ant-tabs-nav-list"
    ) as HTMLElement;
    if (navList) {
      navList.addEventListener("scroll", checkScrollability);
      window.addEventListener("resize", checkScrollability);

      return () => {
        navList.removeEventListener("scroll", checkScrollability);
        window.removeEventListener("resize", checkScrollability);
        clearTimeout(timer);
      };
    }

    return () => clearTimeout(timer);
  }, []);

  const scrollToActiveTab = () => {
    const navList = tabsContainerRef.current?.querySelector(
      ".ant-tabs-nav-list"
    ) as HTMLElement;
    const activeTabElement = tabsContainerRef.current?.querySelector(
      ".ant-tabs-tab-active"
    ) as HTMLElement;
    if (!navList || !activeTabElement) return;

    const navListRect = navList.getBoundingClientRect();
    const activeTabRect = activeTabElement.getBoundingClientRect();

    if (activeTabRect.left < navListRect.left) {
      const scrollAmount = activeTabRect.left - navListRect.left - 10;
      navList.scrollBy({ left: scrollAmount, behavior: "smooth" });
    } else if (activeTabRect.right > navListRect.right) {
      const scrollAmount = activeTabRect.right - navListRect.right + 10;
      navList.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const scrollLeft = () => {
    const navList = tabsContainerRef.current?.querySelector(
      ".ant-tabs-nav-list"
    ) as HTMLElement;
    if (navList) {
      navList.scrollBy({ left: -100, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    const navList = tabsContainerRef.current?.querySelector(
      ".ant-tabs-nav-list"
    ) as HTMLElement;
    if (navList) {
      navList.scrollBy({ left: 100, behavior: "smooth" });
    }
  };

  // Scroll active tab into view when activeTab changes
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToActiveTab();
      checkScrollability();
    }, 150);
    return () => clearTimeout(timer);
  }, [activeTab]);

  const tabsItems = [
    ...visibleTabs,
    ...(dropdownTabs.length > 0
      ? [
          {
            key: "MORE",
            label: (
              <Dropdown
                menu={{
                  items: dropdownMenuItems,
                  onClick: handleDropdownMenuClick,
                }}
                trigger={["click"]}
                placement="bottomLeft"
              >
                <span className={styles.moreTabLabel}>
                  {dropdownTabs.some((tab) => tab.key === activeTab)
                    ? getDropdownLabel()
                    : "Khác"}
                  <DownOutlined className={styles.moreTabIcon} />
                </span>
              </Dropdown>
            ),
          },
        ]
      : []),
  ];

  const displayActiveKey =
    dropdownTabs.length > 0 && dropdownTabs.some((tab) => tab.key === activeTab)
      ? "MORE"
      : activeTab;

  return (
    <div className={styles.tabsContainer} ref={tabsContainerRef}>
      {canScrollLeft && (
        <LeftOutlined
          onClick={scrollLeft}
          className={styles.scrollButtonLeft}
        />
      )}
      <Tabs
        className={styles.conversationTabs}
        activeKey={displayActiveKey}
        items={tabsItems}
        onChange={(key) => {
          if (key !== "MORE") {
            onChange(key as ConversationTabType);
          }
        }}
      />
      {canScrollRight && (
        <RightOutlined
          onClick={scrollRight}
          className={styles.scrollButtonRight}
        />
      )}
    </div>
  );
};
